import React, { useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";
import RiskDetailTabs from "./RiskDetailTabs";
import { formatLocal, parseUtc } from "./socFreshness";
import { FOCUS_RING, SeverityBadge, buttonClass } from "./socUi";
import { EXPLOIT_SIGNALS, formatCvss, publishedText, remediationLabel, riskStatusLabel, severityKey } from "./socVulnerabilities";

// Devices listed before the "+ N more devices" toggle.
const DEVICES_SHOWN = 10;

// Defender's exploit signals as separate badges. All unknown reads "Not available"; known and all
// false reads "None known" - never a "No" that Defender did not say.
export const ExploitBadges = ({ details }) => {
  const on = EXPLOIT_SIGNALS.filter(([k]) => details?.[k] === true);
  if (on.length > 0) {
    return (
      <span className="flex flex-wrap gap-1">
        {on.map(([k, label]) => (
          <span key={k} data-testid={`soc-exploit-${k}`}
            className="inline-flex rounded bg-status-error-bg px-1.5 py-0.5 text-xs font-medium text-status-error">
            {label}
          </span>
        ))}
      </span>
    );
  }
  const anyKnown = EXPLOIT_SIGNALS.some(([k]) => details?.[k] === false);
  return <span className="text-xs text-content-muted">{anyKnown ? "None known" : "Not available"}</span>;
};

const Facts = ({ items, testId }) => (
  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3 lg:grid-cols-5" data-testid={testId}>
    {items.map(([label, value]) => (
      <div key={label} className="min-w-0">
        <dt className="text-xs text-content-muted">{label}</dt>
        <dd className="text-content-primary">{value}</dd>
      </div>
    ))}
  </dl>
);

// One device's open risk for this CVE: its facts, then the All Risks tabs (RiskDetailTabs).
const DeviceDetails = ({ device, risk }) => (
  <div className="space-y-3" data-testid={`soc-cve-device-details-${device.riskId}`}>
    <Facts testId="soc-cve-device-facts" items={[
      ["Severity", <SeverityBadge key="s" severity={severityKey(device.severity)} />],
      ["Risk status", riskStatusLabel(device.status)],
      ["Remediation", remediationLabel(device.remediationStatus)],
      ["First seen", formatLocal(parseUtc(device.firstSeen)) || "Unknown"],
      ["Last seen", formatLocal(parseUtc(device.lastSeen)) || "Unknown"],
    ]} />
    {risk === null && (
      <p className="text-xs text-content-muted" data-testid="soc-cve-device-partial">
        The full risk details couldn't be loaded, so the tabs show what is known here.
      </p>
    )}
    <RiskDetailTabs risk={risk || { id: device.riskId, riskDescription: null }} loading={risk === undefined} />
  </div>
);

// From the org's CVE upgrade (the response has `generation`): one row per device with the software carrying the
// CVE and Defender's first-seen time, and no per-device risk. The whole CVE has one risk, from the risk register's
// upgrade on; it opens here in place (All Risks tabs), never on another page. Before that a note says when it starts.
const SnapshotDetails = ({ cveId, detail, now, allRisks }) => {
  const [riskOpen, setRiskOpen] = useState(false);
  const [showAllDevices, setShowAllDevices] = useState(false);
  const { request, riskFor, version } = allRisks;
  useEffect(() => {
    if (riskOpen) request();
  }, [riskOpen, request, version]);

  const devices = detail.devices || [];
  const hiddenCount = Math.max(0, devices.length - DEVICES_SHOWN);
  const shownDevices = showAllDevices ? devices : devices.slice(0, DEVICES_SHOWN);
  const risk = detail.risk;
  return (
    <div className="space-y-3" data-testid={`soc-cve-details-${cveId}`}>
      {detail.generation?.refreshed && (
        <p className="rounded-md bg-status-warning-bg px-3 py-2 text-xs text-content-primary" data-testid="soc-cve-details-refreshed">
          A newer Defender snapshot has been published since this page loaded. Refresh the page to see it.
        </p>
      )}
      <Facts testId="soc-cve-details-facts" items={[
        ["Severity", <SeverityBadge key="s" severity={severityKey(detail.severity)} />],
        ["CVSS", formatCvss(detail.details?.cvssScore)],
        ["Published", publishedText(detail.details?.publishedOn, now)],
        ["Exploit", <ExploitBadges key="e" details={detail.details} />],
        ["Affected devices", `Open on ${devices.length} ${devices.length === 1 ? "device" : "devices"}`],
      ]} />
      <div className="flex flex-wrap items-center gap-3 rounded-md bg-theme-secondary px-3 py-2 text-sm" data-testid="soc-cve-details-risk">
        {risk ? (
          <>
            <span className="text-content-secondary">{`Risk: ${riskStatusLabel(risk.status)} · Remediation: ${remediationLabel(risk.remediationStatus)}`}</span>
            <button type="button" className={buttonClass("sm")} aria-expanded={riskOpen} onClick={() => setRiskOpen((x) => !x)}
              data-testid="soc-cve-details-risk-toggle">
              {riskOpen ? "Hide risk" : "View risk"}
            </button>
          </>
        ) : (
          <span className="text-content-secondary" data-testid="soc-cve-details-risk-note">{detail.riskNote}</span>
        )}
      </div>
      {risk && riskOpen && (
        <RiskDetailTabs risk={riskFor(risk.riskId) || { id: risk.riskId, riskDescription: null }} loading={riskFor(risk.riskId) === undefined} />
      )}
      <table className="w-full text-left text-sm" data-testid="soc-cve-details-devices">
        <thead>
          <tr className="text-xs text-content-muted">
            <th className="py-1.5 pr-3 font-medium">Device</th>
            <th className="py-1.5 pr-3 font-medium">Software</th>
            <th className="py-1.5 pr-3 font-medium">Severity</th>
            <th className="py-1.5 font-medium" title="First seen by Microsoft Defender">First seen</th>
          </tr>
        </thead>
        <tbody>
          {shownDevices.map((d) => (
            <tr key={d.deviceId} className="border-t border-border align-top" data-testid="soc-cve-device-row">
              <td className="break-all py-2 pr-3 font-medium text-content-primary">{d.device}</td>
              <td className="py-2 pr-3 text-xs text-content-secondary">{(d.software || []).join(", ") || "Not available"}</td>
              <td className="py-2 pr-3"><SeverityBadge severity={severityKey(d.severity)} /></td>
              <td className="py-2 text-content-secondary" title="First seen by Microsoft Defender">
                {formatLocal(parseUtc(d.firstSeen)) || "Not available"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hiddenCount > 0 && (
        <button type="button" onClick={() => setShowAllDevices((x) => !x)} aria-expanded={showAllDevices}
          className={buttonClass("sm")} data-testid="soc-cve-devices-more">
          {showAllDevices ? "Show fewer" : `+ ${hiddenCount} more ${hiddenCount === 1 ? "device" : "devices"}`}
        </button>
      )}
    </div>
  );
};

// One CVE's details, opened under its row in the CVE list (/soc) - the Axaris pattern of expanding in
// place, as All Risks and the incident queue do. From GET /soc/vulnerabilities/{cveId}: the CVE's facts,
// then its affected devices; each device expands under its own row into its risk's All Risks tabs.
// `allRisks` is useAllRiskDetails(): the full risks, read when a device is first opened (again after
// an invalidation, at once if a device is open).
const SocCveDetails = ({ cveId, now = new Date(), allRisks, asOfRunId = null }) => {
  const apiCall = useApi();
  // undefined: loading; null: failed; "gone": no open risk any more; else the detail.
  const [detail, setDetail] = useState(undefined);
  const [openRiskId, setOpenRiskId] = useState(null);
  const [showAllDevices, setShowAllDevices] = useState(false);
  const { request, riskFor, version } = allRisks;

  useEffect(() => {
    let live = true;
    setDetail(undefined);
    // The run the page showed (vulnerabilities.generation.asOfRunId), so the drawer and the list agree.
    const query = asOfRunId ? `?asOfRunId=${encodeURIComponent(asOfRunId)}` : "";
    apiCall("GET", `/soc/vulnerabilities/${encodeURIComponent(cveId)}${query}`)
      .then((data) => { if (live) setDetail(data || "gone"); })
      .catch((err) => { if (live) setDetail(err?.response?.status === 404 ? "gone" : null); });
    return () => { live = false; };
  }, [apiCall, cveId, asOfRunId]);

  useEffect(() => {
    if (openRiskId) request();
  }, [openRiskId, request, version]);

  if (detail === undefined) {
    return (
      <div data-testid="soc-cve-details-loading" className="flex justify-center py-6">
        <div className="h-6 w-6 animate-spin rounded-full border-4 border-border border-t-accent-cyan" />
      </div>
    );
  }
  if (detail === null) return <p className="text-sm text-content-secondary">The affected devices couldn't be loaded.</p>;
  if (detail === "gone") {
    return (
      <p className="text-sm text-content-secondary">
        {asOfRunId
          ? "This CVE isn't in Defender's latest snapshot any more. Refresh the page to update the list."
          : "This CVE has no open risks any more. Refresh the page to update the list."}
      </p>
    );
  }
  if (detail.generation) return <SnapshotDetails cveId={cveId} detail={detail} now={now} allRisks={allRisks} />;

  const devices = detail.devices || [];
  const hiddenCount = Math.max(0, devices.length - DEVICES_SHOWN);
  const shownDevices = showAllDevices ? devices : devices.slice(0, DEVICES_SHOWN);
  return (
    <div className="space-y-3" data-testid={`soc-cve-details-${cveId}`}>
      <Facts testId="soc-cve-details-facts" items={[
        ["Severity", <SeverityBadge key="s" severity={severityKey(detail.severity)} />],
        ["CVSS", formatCvss(detail.details?.cvssScore)],
        ["Published", publishedText(detail.details?.publishedOn, now)],
        ["Exploit", <ExploitBadges key="e" details={detail.details} />],
        ["Affected devices", `Open on ${devices.length} ${devices.length === 1 ? "device" : "devices"}`],
      ]} />
      <table className="w-full text-left text-sm" data-testid="soc-cve-details-devices">
        <thead>
          <tr className="text-xs text-content-muted">
            <th className="py-1.5 pr-3 font-medium">Device</th>
            <th className="py-1.5 pr-3 font-medium">Severity</th>
            <th className="py-1.5 pr-3 font-medium">Risk status</th>
            <th className="py-1.5 pr-3 font-medium">Remediation</th>
            <th className="py-1.5 font-medium">First seen</th>
          </tr>
        </thead>
        <tbody>
          {shownDevices.map((d) => {
            const isOpen = openRiskId === d.riskId;
            return (
              <React.Fragment key={d.riskId}>
                <tr className={`border-t border-border align-top ${isOpen ? "bg-accent-cyan-muted" : ""}`}>
                  <td className="py-2 pr-3">
                    <button
                      type="button"
                      onClick={() => setOpenRiskId((cur) => (cur === d.riskId ? null : d.riskId))}
                      aria-expanded={isOpen}
                      title={isOpen ? "Hide details" : "Show details"}
                      className={`break-all rounded text-left font-medium text-content-primary hover:text-accent-cyan hover:underline ${FOCUS_RING}`}
                    >
                      {d.device}
                    </button>
                  </td>
                  <td className="py-2 pr-3"><SeverityBadge severity={severityKey(d.severity)} /></td>
                  <td className="py-2 pr-3 text-content-secondary">{riskStatusLabel(d.status)}</td>
                  <td className="py-2 pr-3 text-content-secondary">{remediationLabel(d.remediationStatus)}</td>
                  <td className="py-2 text-content-secondary">{formatLocal(parseUtc(d.firstSeen))}</td>
                </tr>
                {isOpen && (
                  <tr className="border-t border-border">
                    <td colSpan={5} className="py-3">
                      <DeviceDetails device={d} risk={riskFor(d.riskId)} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {hiddenCount > 0 && (
        <button type="button" onClick={() => setShowAllDevices((x) => !x)} aria-expanded={showAllDevices}
          className={buttonClass("sm")} data-testid="soc-cve-devices-more">
          {showAllDevices ? "Show fewer" : `+ ${hiddenCount} more ${hiddenCount === 1 ? "device" : "devices"}`}
        </button>
      )}
    </div>
  );
};

export default SocCveDetails;
